import { AsyncButton } from '@c42/component-library';
import { AsyncState, isError, isLoading, isNotStarted } from '@c42/constants';
import CoreClient from '@c42/core-client';
import { TextField } from '@mui/material';
import * as React from 'react';
import { useHistory } from 'react-router';
import Text from '../../../shared/Text';
import { MATCH_ALL_BACKSLASHES } from '../../constants/regexes';
import { DEFAULT_REDIRECT_AFTER_LOGIN, SSO_INDEX } from '../../constants/routes';
import { sendEmptyTextIfNull } from '../../utils/input-handlers';
import FormValidationMessage from '../form-validation-message';
import { API_AUTHORITY_LOGIN_CONFIG_PATH } from '../providers/auth/constants';
import { SsoLoginUrl, STEP_PASSWORD } from '../providers/auth/context';
import { useAuthData, useSetAuthData } from '../providers/auth/hooks';
import { useResetPasswordData } from '../providers/resetPassword/hooks';
import { useRoutingData, useSetRoutingData } from '../providers/routing/hooks';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from '../shared-form-components';

const LoginStepUsername: React.FC = () => {
  const [submitState, setSubmitState] = React.useState(AsyncState.NOT_STARTED);

  const history = useHistory();
  const setRoutingData = useSetRoutingData();
  const setAuthData = useSetAuthData();
  const { username } = useAuthData();
  const { redirectParam, usernameParam } = useRoutingData();
  const { notification } = useResetPasswordData();

  const changeUsernameHandler: React.FormEventHandler = (event) => {
    event.persist?.();
    setAuthData((current) => ({
      ...current,
      username: (event.target as HTMLInputElement).value,
    }));
  };

  const handleSubmitUsername = async (username: string) => {
    try {
      setSubmitState(AsyncState.LOADING);

      const {
        location: { protocol, host },
      } = window;
      const SERVER_ADDRESS = `${protocol}//${host}`;

      let redirectPath =
        !redirectParam || redirectParam.indexOf('/login') === 0
          ? DEFAULT_REDIRECT_AFTER_LOGIN
          : redirectParam;
      // Remove any potential backslashes from the redirectPath (PL-105140)
      redirectPath = redirectPath.replace(MATCH_ALL_BACKSLASHES, '');
      const redirectUrl = `/login/#/login?to=${encodeURIComponent(redirectPath)}`;

      const params = {
        query: {
          redirectUrl,
          url: SERVER_ADDRESS,
          username,
        },
        options: {
          url: API_AUTHORITY_LOGIN_CONFIG_PATH,
        },
      };

      const responseConfiguration: { loginType: string; ssoLoginUrls: SsoLoginUrl[] } =
        await CoreClient.LoginConfiguration.find(params);
      const { loginType, ssoLoginUrls } = responseConfiguration;

      const type = String(loginType).toLowerCase();

      // These login type strings are derived from Core:
      // https://stash.corp.code42.com/projects/SVR/repos/core/browse/pro_core/src/impl/java/com/code42/login/LoginType.java
      const ssoTypes = ['private_sso', 'cloud_sso', 'saml_sso'];
      const isSSO = ssoTypes.indexOf(type) > -1;
      const isUsingTotp = type === 'local_2fa';

      setAuthData((current) => ({
        ...current,
        isUsingTotp,
        ssoLoginUrls,
      }));

      if (isSSO) {
        history.push(SSO_INDEX);
      } else {
        setAuthData((current) => ({
          ...current,
          currentStep: STEP_PASSWORD,
        }));
      }
    } catch {
      setSubmitState(AsyncState.ERROR);
    }
  };

  const handleSubmitForm: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    handleSubmitUsername(username);
  };

  React.useEffect(() => {
    if (usernameParam !== null) {
      setAuthData((current) => ({
        ...current,
        username: usernameParam,
      }));
      setRoutingData((current) => ({
        ...current,
        usernameParam: null,
      }));
      handleSubmitUsername(usernameParam);
    }
  }, []);

  const isInputDirty = username !== null;
  const isUsernameEmpty = !username;

  let error: string;
  if (isError(submitState)) {
    error = Text.get('login_config_error_generic');
  }

  const errorText =
    isInputDirty && isUsernameEmpty && !isNotStarted(submitState)
      ? Text.get('_validate_username_or_email')
      : '';

  return (
    <FormWrapper>
      <form
        onSubmit={handleSubmitForm}
        name="login-step1-form"
        data-test="login-step-username-form"
        noValidate
      >
        <FormValidationMessage
          message={error || notification}
          type={error ? 'error' : 'success'}
          dataTest="validation-msg"
        />

        <FormFieldWrapper>
          <TextField
            autoComplete="off"
            autoFocus
            disabled={isLoading(submitState)}
            error={Boolean(errorText)}
            fullWidth
            helperText={errorText}
            id="username"
            inputProps={{ 'data-test': 'username' }}
            label={Text.get('_placeholder_username_or_email')}
            onBlur={sendEmptyTextIfNull.bind(null, changeUsernameHandler, username)}
            onChange={changeUsernameHandler}
            value={username || ''}
          />
        </FormFieldWrapper>

        <FormSubmitWrapper>
          <AsyncButton
            data-test="submit"
            isLoading={isLoading(submitState)}
            type="submit"
            variant="contained"
          >
            {Text.get('login_button_sign_in')}
          </AsyncButton>
        </FormSubmitWrapper>
      </form>
    </FormWrapper>
  );
};

export default LoginStepUsername;
